// Tin mới sản phẩm — kho trạng thái DÙNG CHUNG cho chuông, khung đọc bài, trang Tin mới.
//
// Vì sao là kho module (useSyncExternalStore) chứ không phải React Context: chuông nằm ở thanh
// trên cùng, khung đọc bài mount một lần ở vỏ trang, thẻ chat mở bài qua `openEntityLink` (hàm
// thuần, không hook) — ba nơi không cùng cây Provider. Kho treo trên `globalThis` để bundle bị
// nạp hai lần (xem [[reference_vite_dedup_dev_masks_prod]]) vẫn là MỘT kho: số trên chuông và
// chấm «chưa đọc» trong trang không bao giờ lệch nhau.
//
// Hợp đồng API (Core `/api/product-news/*` = Work `/api/v1/product-news/*`):
//   GET  /feed?limit&before&kind&unread → { items:[{id,title,excerpt,cover_image_url,kind,
//                                          published_at,is_read}], unread_count, has_more }
//   GET  /unread-count                  → { count }
//   GET  /:id                           → bài đầy đủ (thêm content_html, app_path, app_label,
//                                          doc_url, doc_label)
//   POST /:id/read · POST /read-all     → { unread_count }
import { useSyncExternalStore } from 'react';
import { fetchFeed, markAllNewsRead, markNewsRead } from './productNewsApi';

const EMPTY = Object.freeze({
  items: [],
  unread: 0,
  hasMore: false,
  loaded: false,
  loading: false,
  error: false,
  openId: null,
  openPreview: null,
});

const G = (() => {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!g.__cnvProductNews) {
    g.__cnvProductNews = { state: EMPTY, listeners: new Set(), inflight: null, lastFetchAt: 0 };
  }
  return g.__cnvProductNews;
})();

function emit() {
  for (const l of G.listeners) {
    try { l(); } catch { /* một listener lỗi không được chặn các listener khác */ }
  }
}

function setState(patch) {
  G.state = { ...G.state, ...patch };
  emit();
}

export function getProductNewsState() { return G.state; }

function subscribe(l) {
  G.listeners.add(l);
  return () => G.listeners.delete(l);
}

export function useProductNews() {
  return useSyncExternalStore(subscribe, getProductNewsState, getProductNewsState);
}

const FEED_LIMIT = 20;
const MIN_REFRESH_MS = 30 * 1000;

/**
 * Nạp lại trang đầu của bảng tin (cho chuông). Gọi dồn dập (mount + focus + interval) thì chỉ
 * một lượt bay thật; `force` bỏ qua ngưỡng 30 giây (sau khi đánh dấu đọc, sau khi mở bài).
 * Lỗi mạng KHÔNG xoá danh sách đang có — chuông giữ số cũ còn hơn nhảy về 0 rồi lại nhảy lên.
 */
export async function refreshProductNews({ force = false } = {}) {
  if (G.inflight) return G.inflight;
  if (!force && G.state.loaded && Date.now() - G.lastFetchAt < MIN_REFRESH_MS) return G.state;
  setState({ loading: true });
  G.inflight = (async () => {
    try {
      const d = (await fetchFeed({ limit: FEED_LIMIT })) || {};
      G.lastFetchAt = Date.now();
      setState({
        items: Array.isArray(d.items) ? d.items : [],
        unread: Number(d.unread_count) || 0,
        hasMore: !!d.has_more,
        loaded: true,
        loading: false,
        error: false,
      });
    } catch {
      setState({ loaded: true, loading: false, error: true });
    } finally {
      G.inflight = null;
    }
    return G.state;
  })();
  return G.inflight;
}

/** Đánh dấu một bài đã đọc — lạc quan: chấm đỏ + số trên chuông đổi ngay, máy chủ trả số thật sau. */
export async function markProductNewsRead(id) {
  if (!id) return;
  const hit = G.state.items.find((i) => i.id === id);
  if (hit && !hit.is_read) {
    setState({
      items: G.state.items.map((i) => (i.id === id ? { ...i, is_read: true } : i)),
      unread: Math.max(0, G.state.unread - 1),
    });
  }
  try {
    const r = await markNewsRead(id);
    if (r && r.unread_count != null) setState({ unread: Number(r.unread_count) || 0 });
  } catch { /* lần mở sau sẽ thử lại — không làm phiền người đọc bằng lỗi này */ }
  emitReadChange([id]);
}

export async function markAllProductNewsRead() {
  setState({ items: G.state.items.map((i) => (i.is_read ? i : { ...i, is_read: true })), unread: 0 });
  try {
    const r = await markAllNewsRead();
    if (r && r.unread_count != null) setState({ unread: Number(r.unread_count) || 0 });
  } catch {
    refreshProductNews({ force: true });
  }
  emitReadChange(null);
}

// Trang Tin mới giữ danh sách phân trang RIÊNG (dài hơn chuông) — nghe sự kiện này để tô lại
// chấm «chưa đọc» khi người dùng đọc bài từ chuông/thẻ chat. `ids = null` ⇒ đã đọc hết.
const READ_EVENT = 'cnv:product-news:read';
function emitReadChange(ids) {
  try { window.dispatchEvent(new CustomEvent(READ_EVENT, { detail: { ids } })); } catch { /* SSR/test */ }
}
export function onProductNewsRead(fn) {
  const h = (e) => fn(e?.detail?.ids ?? null);
  window.addEventListener(READ_EVENT, h);
  return () => window.removeEventListener(READ_EVENT, h);
}

/**
 * Mở khung đọc bài (ProductNewsHost vẽ nó). `preview` = bản tóm tắt đã có trong tay (dòng ở
 * chuông/trang) để khung hiện tiêu đề + ảnh bìa ngay trong lúc chờ nội dung đầy đủ.
 */
export function openProductNews(id, preview = null) {
  if (!id) return;
  setState({ openId: String(id), openPreview: preview || G.state.items.find((i) => i.id === id) || null });
}

export function closeProductNews() {
  setState({ openId: null, openPreview: null });
}
